import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { useToaster } from '@components/toaster';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';
import { seconds } from '@utils/milliseconds';
import { isConnectionError } from '@utils/requests';

import {
  activeFollowers,
  ApplicationformData,
  bulkGrantAccessToApplicants,
  bulkRemoveApplicationAccess,
  create,
  deleteLanguage,
  duplicateUnhandled,
  edit,
  editRewards,
  editSocial,
  EmailTemplateData,
  exportApplicants,
  ExportApplicantsData,
  FollowerResponse,
  followers,
  getApplicationForm,
  getEmailTemplate,
  getJobOrder,
  getRewards,
  getSocial,
  loadApplicationAccessForJob,
  loadFromDbUnhandled,
  moveToArchiveUnhandled,
  moveToDraftsUnhandled,
  patchApplicationForm,
  pinJob,
  publishUnhandled,
  searchJobs,
  SocialData,
  SubmitError,
  unpinJob,
  updateEmailTemplate,
  unpublishUnhandled,
} from 'api/jobs/requests';
import { reportRequestError } from 'api/request';
import { ELASTICSEARCH_REINDEX_DELAY_MS } from 'helpers/elasticsearch';
import { jobListTab } from 'helpers/job-lifecycle';
import { useStore } from 'models/Provider';
import type { CreateJobType, JobListOrdering, JobListRow, JobState } from 'types/jobs';

const JOBS_QUERY_KEY = 'jobs';

type JobListPages = InfiniteData<Awaited<ReturnType<typeof searchJobs>>>;

const toFirstPage = (data: JobListPages | undefined) =>
  data && { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) };

// The default pauses offline requests until reconnect; fail at once so the user is told.
const FAIL_WHEN_OFFLINE = 'always';

export const useJobActionFailureSubtitle = () => {
  const { t } = useScopedTranslate(JobHooksLocale);
  return useCallback(
    (error: unknown, title?: string) => (isConnectionError(error) ? t('offline') : title),
    [t],
  );
};

type LifecycleVariables = { jobId: number; title?: string };

/** Counted from loaded rows, not pages × limit, so a short page cannot drift the offset. */
export const nextJobListOffset = (
  lastPage: Awaited<ReturnType<typeof searchJobs>>,
  pages: Awaited<ReturnType<typeof searchJobs>>[],
) => (lastPage.next ? pages.reduce((loaded, page) => loaded + page.results.length, 0) : undefined);

export const useJobList = (params: {
  status: JobState;
  ordering: JobListOrdering;
  search: string;
}) => {
  const { status, ordering, search } = params;
  const queryClient = useQueryClient();

  // Trimmed to its first page off screen; an in-flight refetch would write every page back.
  useEffect(
    () => () => {
      const queryKey = [JOBS_QUERY_KEY, status, ordering, search];
      const trim = () => queryClient.setQueryData<JobListPages>(queryKey, toFirstPage);
      if (queryClient.isFetching({ queryKey, exact: true })) {
        queryClient.cancelQueries({ queryKey, exact: true }).then(trim);
      }
      trim();
    },
    [queryClient, status, ordering, search],
  );

  return useInfiniteQuery({
    queryKey: [JOBS_QUERY_KEY, status, ordering, search],
    queryFn: ({ pageParam = 0 }) => searchJobs({ status, ordering, search, offset: pageParam }),
    getNextPageParam: nextJobListOffset,
    keepPreviousData: true,
    networkMode: FAIL_WHEN_OFFLINE,
    staleTime: seconds(30),
    // The job page changes the counts and has no way to tell the list.
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    // `unhandledRequest` reports nothing, so a failing list would be invisible in production.
    onError: reportRequestError,
  });
};

/** Patches the row in while Elasticsearch catches up, only where it certainly sorts first. */
const useMoveJobBetweenLists = () => {
  const queryClient = useQueryClient();
  const invalidateJobListAfterReindex = useInvalidateJobListAfterReindex();
  return useCallback(
    ({
      jobId,
      changes,
      newestBy,
    }: {
      jobId: number;
      changes: Partial<JobListRow>;
      newestBy?: 'date_published' | 'date_archived';
    }) => {
      let moved: JobListRow | undefined;

      queryClient.setQueriesData<JobListPages>([JOBS_QUERY_KEY], (data) => {
        if (!data) return data;
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            results: page.results.filter((job) => {
              if (job.id !== jobId) return true;
              moved = job;
              return false;
            }),
          })),
        };
      });

      if (moved && newestBy) {
        const row = { ...moved, ...changes };
        const destination = jobListTab(row);
        queryClient.setQueriesData<JobListPages>(
          {
            queryKey: [JOBS_QUERY_KEY, destination, `-${newestBy}`, ''],
            exact: true,
          },
          (data) => {
            if (!data || data.pages.length === 0) return data;
            const [first, ...rest] = data.pages;
            return {
              ...data,
              pages: [{ ...first, results: [row, ...first.results] }, ...rest],
            };
          },
        );
      }

      invalidateJobListAfterReindex();
    },
    [queryClient, invalidateJobListAfterReindex],
  );
};

/** `pages/Job` renders from MST, and `Job.load()` skips once `detailLoaded` is set. */
const useMarkJobDetailStale = () => {
  const { jobStore } = useStore();
  return useCallback((jobId: number) => jobStore.markDetailStale(jobId), [jobStore]);
};

/** Refetching only the first page would skip or repeat rows; off screen, each page is too costly to refetch. */
export const useInvalidateJobList = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.setQueriesData<JobListPages>(
      { queryKey: [JOBS_QUERY_KEY], type: 'inactive' },
      toFirstPage,
    );
    return queryClient.invalidateQueries([JOBS_QUERY_KEY]);
  }, [queryClient]);
};

const pendingListRefresh = new WeakMap<QueryClient, number>();

/** Waits for Elasticsearch to index the change; one shared timer, so an earlier refetch can't undo a later patch. */
export const useInvalidateJobListAfterReindex = () => {
  const queryClient = useQueryClient();
  const invalidateJobList = useInvalidateJobList();
  return useCallback(() => {
    window.clearTimeout(pendingListRefresh.get(queryClient));
    pendingListRefresh.set(
      queryClient,
      window.setTimeout(() => {
        pendingListRefresh.delete(queryClient);
        invalidateJobList();
      }, ELASTICSEARCH_REINDEX_DELAY_MS),
    );
  }, [queryClient, invalidateJobList]);
};

export const jobQueryOptions = (id: number) => ({
  queryKey: ['job', id],
  queryFn: () => loadFromDbUnhandled(id),
});

export const useGetJob = (id: number, enabled?: boolean) => {
  const { t } = useScopedTranslate(JobHooksLocale);
  const { showError } = useToaster();
  return useQuery({
    ...jobQueryOptions(id),
    enabled,
    onError: () => {
      showError(t('getJobError'));
    },
  });
};

export const useCreateJob = () => {
  const { showSuccess, showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const navigate = useNavigate();
  const invalidateJobListAfterReindex = useInvalidateJobListAfterReindex();
  return useMutation((data: CreateJobType) => create(data), {
    onSuccess: (response) => {
      invalidateJobListAfterReindex();
      showSuccess(t('jobCreated'));
      navigate(`/job/${response.data.id}/edit/form`, { state: 'override' });
    },
    onError: () => {
      showError(t('createJobError'));
    },
  });
};

export const useUpdateJob = () => {
  const { showSuccess, showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  const invalidateJobListAfterReindex = useInvalidateJobListAfterReindex();
  return useMutation((data: { jobId: number; job: CreateJobType }) => edit(data.jobId, data.job), {
    onSuccess: async (_, variables) => {
      await queryClient.refetchQueries(['job', variables.jobId]);
      await queryClient.refetchQueries(['job-application-form', variables.jobId]);
      invalidateJobListAfterReindex();
      showSuccess(t('jobUpdated'));
    },
    onError: () => {
      showError(t('updateJobError'));
    },
  });
};

export const useDuplicateJob = () => {
  const invalidateJobListAfterReindex = useInvalidateJobListAfterReindex();
  return useMutation(
    (props: { jobId: number; onSuccess: () => void; onError: (error: unknown) => void }) =>
      duplicateUnhandled(props.jobId),
    {
      mutationKey: ['duplicate-job'],
      networkMode: FAIL_WHEN_OFFLINE,
      onSuccess: (_, { onSuccess }) => {
        invalidateJobListAfterReindex();
        onSuccess?.();
      },
      onError: (error, { onError }) => {
        reportRequestError(error);
        onError?.(error);
      },
    },
  );
};

export const useGetApplicationForm = (id: number, enabled?: boolean) => {
  const { showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  return useQuery({
    queryKey: ['job-application-form', id],
    queryFn: () => getApplicationForm(id),
    enabled,
    onError: () => {
      showError(t('getApplicationFormError'));
    },
  });
};

export const usePatchApplicationForm = () => {
  const { showSuccess, showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  const invalidateJobListAfterReindex = useInvalidateJobListAfterReindex();
  return useMutation(
    (jobdata: { id: number; data: ApplicationformData }) =>
      patchApplicationForm(jobdata.id, jobdata.data),
    {
      mutationKey: ['patch-application-form'],
      onSuccess: async (_, variables) => {
        showSuccess(t('applicationFormUpdated'));
        // The list's publish check reads each language's form from the card.
        invalidateJobListAfterReindex();
        await queryClient.refetchQueries(['job', variables.id]);
        await queryClient.refetchQueries(['job-application-form', variables.id]);
      },
      onError: () => {
        showError(t('updateApplicationFormError'));
      },
    },
  );
};

export const useGetActiveFollowers = (id: number, onSuccess?: (data: FollowerResponse) => void) => {
  return useQuery({
    queryKey: ['job', 'followers', id],
    queryFn: () => activeFollowers(id),
    onSuccess,
  });
};

export const useUpdateFollowers = () => {
  const { showSuccess, showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  const invalidateJobListAfterReindex = useInvalidateJobListAfterReindex();
  return useMutation(
    (data: { jobId: number; followerIds: number[]; appFollowerIds: number[] }) =>
      followers(data.jobId, { followers: data.followerIds, appFollowers: data.appFollowerIds }),
    {
      mutationKey: ['update-followers'],
      onSuccess: (_, variables) => {
        queryClient.refetchQueries(['job', 'followers', variables.jobId]);
        queryClient.refetchQueries(['job', variables.jobId]);
        // The list card shows a follower count in its "Access" column.
        invalidateJobListAfterReindex();
        showSuccess(t('accessUpdated'));
      },
      onError: () => {
        showError(t('accessUpdateError'));
      },
    },
  );
};

export const useGetSocial = (jobId: number, onSuccess?: (data: SocialData) => void) => {
  return useQuery({
    queryKey: ['job', 'social', jobId],
    queryFn: () => getSocial(jobId),
    onSuccess,
  });
};

export const useEditSocial = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { jobId: number; image: any; title: string; description: string }) => {
      const formData = new FormData();
      formData.set('title', data.title);
      data.image && formData.set('image', data.image);
      formData.set('description', data.description);
      return editSocial(data.jobId, formData);
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries(['job', 'social', variables.jobId]);
        queryClient.refetchQueries(['job', 'rewards', variables.jobId]);
      },
    },
  );
};

export const useGetRewards = (jobId: number, onSuccess?: () => void) => {
  return useQuery({
    queryKey: ['job', 'rewards', jobId],
    queryFn: () => getRewards(jobId),
    onSuccess,
  });
};

export const useEditRewards = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { jobId: number; hasRewards: boolean; rewardsTitle?: string; rewards?: string }) =>
      editRewards(data.jobId, {
        hasRewards: data.hasRewards,
        rewardsTitle: data.rewardsTitle,
        rewards: data.rewards,
      }),
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries(['job', 'rewards', variables.jobId]);
      },
    },
  );
};

export const useGetEmailTemplate = (
  jobId: number,
  onSuccess?: (data: EmailTemplateData) => void,
) => {
  return useQuery({
    queryKey: ['job', 'email-template', jobId],
    queryFn: () => getEmailTemplate(jobId),
    onSuccess,
  });
};

export const useUpdateEmailTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { jobId: number; useCompanyEmailTemplate: boolean; contentHtml: string }) =>
      updateEmailTemplate(data.jobId, data),
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries(['job', 'email-template', variables.jobId]);
      },
    },
  );
};

export const useDeleteLanguage = () => {
  const { showError, showSuccess } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  const invalidateJobListAfterReindex = useInvalidateJobListAfterReindex();
  return useMutation(
    (data: { jobId: number; langKey: CareerSiteLanguageType }) =>
      deleteLanguage(data.jobId, data.langKey),
    {
      mutationKey: ['delete-language'],
      onSuccess: (response, variables) => {
        if (response.error && response.status === 400) {
          if (response.data === 'This language currently has applicants and cannot be deleted') {
            showError(t('deleteLanguageApplicantError'));
          }
        } else {
          showSuccess(t('languageDeleted'));
        }
        queryClient.refetchQueries(['job-application-form', variables.jobId]);
        queryClient.refetchQueries(['job', variables.jobId]);
        // The list card resolves its title from the job's languages.
        invalidateJobListAfterReindex();
      },
      onError: () => {
        showError(t('deleteLanguageError'));
      },
    },
  );
};

export const usePublishJob = () => {
  const { t } = useScopedTranslate(JobHooksLocale);
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  const moveJobBetweenLists = useMoveJobBetweenLists();
  const markJobDetailStale = useMarkJobDetailStale();
  const failureSubtitle = useJobActionFailureSubtitle();
  return useMutation(({ jobId }: LifecycleVariables) => publishUnhandled(jobId), {
    mutationKey: ['publish-job'],
    networkMode: FAIL_WHEN_OFFLINE,
    onSuccess: (_, { jobId, title }) => {
      queryClient.refetchQueries(['job', jobId]);
      moveJobBetweenLists({
        jobId,
        changes: {
          isPublished: true,
          isArchived: false,
          datePublished: new Date().toISOString(),
          dateScheduledPublish: null,
        },
        newestBy: 'date_published',
      });
      markJobDetailStale(jobId);
      showSuccess(t('jobPublished'), { subtitle: title });
    },
    onError: (error, { title }) => {
      reportRequestError(error);
      showError(t('jobPublishedError'), { subtitle: failureSubtitle(error, title) });
    },
  });
};

export const useUnpublishJob = () => {
  const { showError, showSuccess } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  const moveJobBetweenLists = useMoveJobBetweenLists();
  const markJobDetailStale = useMarkJobDetailStale();
  const failureSubtitle = useJobActionFailureSubtitle();
  return useMutation(({ jobId }: LifecycleVariables) => unpublishUnhandled(jobId), {
    mutationKey: ['un-publish-job'],
    networkMode: FAIL_WHEN_OFFLINE,
    onSuccess: (_, { jobId, title }) => {
      queryClient.refetchQueries(['job', jobId]);
      moveJobBetweenLists({ jobId, changes: { isPublished: false, isArchived: false } });
      markJobDetailStale(jobId);
      showSuccess(t('jobUnpublished'), { subtitle: title });
    },
    onError: (error, { title }) => {
      reportRequestError(error);
      showError(t('jobUnpublishedError'), { subtitle: failureSubtitle(error, title) });
    },
  });
};

export const useExportApplicants = () => {
  const { showError } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  return useMutation((data: ExportApplicantsData) => exportApplicants(data), {
    mutationKey: ['export-applicants'],
    onError: () => {
      showError(t('exportError'));
    },
  });
};

export const useArchiveJob = () => {
  const { showError, showSuccess } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  const moveJobBetweenLists = useMoveJobBetweenLists();
  const markJobDetailStale = useMarkJobDetailStale();
  const failureSubtitle = useJobActionFailureSubtitle();
  return useMutation(({ jobId }: LifecycleVariables) => moveToArchiveUnhandled(jobId), {
    mutationKey: ['archive-job'],
    networkMode: FAIL_WHEN_OFFLINE,
    onSuccess: (_, { jobId, title }) => {
      queryClient.refetchQueries(['job', jobId]);
      moveJobBetweenLists({
        jobId,
        changes: {
          isArchived: true,
          isPublished: false,
          dateArchived: new Date().toISOString(),
          dateScheduledPublish: null,
        },
        newestBy: 'date_archived',
      });
      markJobDetailStale(jobId);
      showSuccess(t('archivedSuccess'), { subtitle: title });
    },
    onError: (error, { title }) => {
      reportRequestError(error);
      showError(t('archivedError'), { subtitle: failureSubtitle(error, title) });
    },
  });
};

export const useMoveToDrafts = () => {
  const { showError, showSuccess } = useToaster();
  const { t } = useScopedTranslate(JobHooksLocale);
  const queryClient = useQueryClient();
  const moveJobBetweenLists = useMoveJobBetweenLists();
  const markJobDetailStale = useMarkJobDetailStale();
  const failureSubtitle = useJobActionFailureSubtitle();
  return useMutation(({ jobId }: LifecycleVariables) => moveToDraftsUnhandled(jobId), {
    mutationKey: ['draft-job'],
    networkMode: FAIL_WHEN_OFFLINE,
    onSuccess: (_, { jobId, title }) => {
      queryClient.refetchQueries(['job', jobId]);
      moveJobBetweenLists({ jobId, changes: { isPublished: false, isArchived: false } });
      markJobDetailStale(jobId);
      showSuccess(t('movedToDraftSuccess'), { subtitle: title });
    },
    onError: (error, { title }) => {
      reportRequestError(error);
      showError(t('movedToDraftError'), { subtitle: failureSubtitle(error, title) });
    },
  });
};

export const useJobOrderingList = () => {
  const { showError } = useToaster();
  const { t } = useStore();
  return useQuery({
    queryKey: ['job-order-data'],
    queryFn: getJobOrder,
    onError: () => {
      showError(t('company.settings.joborder.toast.get.error'));
    },
  });
};

export const usePinJob = () => {
  const { current, t } = useStore();
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation(
    (data: { jobId: number; ordering: number }) => pinJob(data.jobId, data.ordering),
    {
      mutationKey: ['pin-job'],
      onSuccess: () => {
        current.info.isEnterprise
          ? queryClient.invalidateQueries(['department-order-data'])
          : queryClient.invalidateQueries(['job-order-data']);
        showSuccess(t('company.settings.joborder.toast.job'));
      },
      onError: (error: SubmitError) => {
        showError(
          error.response
            ? (error.response.data.error ?? error.response.data.ordering)
            : error.message,
        );
      },
    },
  );
};

export const useUnPinJob = () => {
  const { current, t } = useStore();
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation((jobId: number) => unpinJob(jobId), {
    mutationKey: ['unpin-job'],
    onSuccess: () => {
      current.info.isEnterprise
        ? queryClient.invalidateQueries(['department-order-data'])
        : queryClient.invalidateQueries(['job-order-data']);
      showSuccess(t('company.settings.joborder.toast.job'));
    },
    onError: (error: SubmitError) => {
      showError(error.response ? error.response.data.error : error.message);
    },
  });
};

export const useUserApplicationAccessForJob = (jobId: number, userId: number) => {
  return useQuery({
    queryKey: ['user-application-access-job', jobId, userId],
    queryFn: () => loadApplicationAccessForJob(jobId, userId),
  });
};

export const useBulkGrantAccessToApplicants = (
  options?: UseMutationOptions<
    AxiosResponse,
    unknown,
    { jobId: number; applications: string[]; users: number[] }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation(bulkGrantAccessToApplicants, {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.refetchQueries(['user-application-access-job', variables.jobId]);
      queryClient.refetchQueries(['job', 'followers', variables.jobId]);
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useBulkRemoveApplicationAccess = (
  options?: UseMutationOptions<
    AxiosResponse,
    unknown,
    { jobId: number; applications: string[]; users: number[] }
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation(bulkRemoveApplicationAccess, {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.refetchQueries(['user-application-access-job', variables.jobId]);
      queryClient.refetchQueries(['job', 'followers', variables.jobId]);
      options?.onSuccess?.(data, variables, context);
    },
  });
};

const JobHooksLocale = {
  en: {
    jobCreated: 'Job created as draft',
    jobUpdated: 'Job updated successfully',
    deleteLanguageApplicantError: 'This language currently has applicants and cannot be deleted',
    languageDeleted: 'Language deleted successfully',
    applicationFormUpdated: 'Application form updated successfully',
    accessUpdated: 'Access for job updated successfully',
    archivedSuccess: 'Job archived successfully',
    archivedError: 'Job could not be archived',
    offline: 'You seem to be offline, please check your connection and try again',
    jobUnpublished: 'Job unpublished successfully',
    jobUnpublishedError: 'Job could not be unpublished',
    jobPublished: 'Job published successfully',
    jobPublishedError: 'Job could not be published',
    movedToDraftSuccess: 'Job moved to drafts successfully',
    movedToDraftError: 'Job could not be moved to drafts',
    getApplicationFormError: 'Could not load job application form',
    updateApplicationFormError: 'Could not update job application form',
    createJobError: 'Could not create job',
    updateJobError: 'Could not update job',
    accessUpdateError: 'Could not update job access',
    deleteLanguageError: 'Could not delete language',
    exportError: 'Could not export applicants',
    getJobError: 'Could not get job',
  },
  is: {
    jobCreated: 'Starf búið til sem drög',
    jobUpdated: 'Starf hefur verið uppfært',
    deleteLanguageApplicantError: 'Þetta tungumál hefur umsækjendur og getur ekki verið eytt',
    languageDeleted: 'Tungumál fjarlægt',
    applicationFormUpdated: 'Umsóknarform hefur verið uppfært',
    accessUpdated: 'Aðgangur starfs hefur verið uppfærður',
    archivedSuccess: 'Starf fært í geymslu',
    archivedError: 'Ekki tókst að færa starf í geymslu',
    offline: 'Þú virðist vera án nettengingar, athugaðu tenginguna og reyndu aftur',
    jobUnpublished: 'Starf fært í drög',
    jobUnpublishedError: 'Ekki tókst að færa starf í drög',
    jobPublished: 'Starf birt',
    jobPublishedError: 'Ekki tókst að birta starf',
    movedToDraftSuccess: 'Starf fært í drög',
    movedToDraftError: 'Ekki tókst að færa starf í drög',
    getApplicationFormError: 'Ekki tókst að sækja umsóknarform',
    updateApplicationFormError: 'Ekki tókst að uppfæra umsóknarform',
    createJobError: 'Ekki tókst að búa til starf',
    updateJobError: 'Ekki tókst að uppfæra starf',
    accessUpdateError: 'Ekki tókst að uppfæra aðgang',
    deleteLanguageError: 'Ekki tókst að eyða tungumáli',
    exportError: 'Ekki tókst að hlaða niður umsækjendum',
    getJobError: 'Ekki tókst að sækja starf',
  },
};
